import { API_BASE as BASE_URL, getAuthHeaders, apiFetch } from './api-fetch';
import { API, buildPath } from './api-endpoints';
import { SellerProperty } from '../types/seller';

// Get all seller properties
export async function getSellerProperties(): Promise<SellerProperty[]> {
  try {
    const response = await apiFetch(`${BASE_URL}${API.sellerProperties.list.path}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      let message = 'Failed to fetch seller properties';
      try { const error = await response.json(); message = error.error || message; } catch {}
      throw new Error(message);
    }

    const data = await response.json();
    return data.data?.properties ?? [];
  } catch (error) {
    console.error('Error fetching seller properties:', error);
    throw error;
  }
}

// Get seller properties by seller ID
export async function getSellerPropertiesBySeller(sellerId: string): Promise<SellerProperty[]> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellerProperties.getBySeller.path, { sellerId })}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      let message = 'Failed to fetch seller properties';
      try { const error = await response.json(); message = error.error || message; } catch {}
      throw new Error(message);
    }

    const data = await response.json();
    return data.data?.properties ?? [];
  } catch (error) {
    console.error('Error fetching seller properties:', error);
    throw error;
  }
}

// Get single seller property
export async function getSellerProperty(id: string): Promise<SellerProperty> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellerProperties.getById.path, { id })}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Failed to fetch seller property');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching seller property:', error);
    throw error;
  }
}

// Create seller property
export async function createSellerProperty(propertyData: Partial<SellerProperty>): Promise<SellerProperty> {
  try {
    const response = await apiFetch(`${BASE_URL}${API.sellerProperties.create.path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(propertyData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create seller property');
    }

    const data = await response.json();
    const prop = data.data?.property ?? data.data;
    // Normalize id: backend returns propertyId (hex), not id
    return { ...prop, id: prop.id ?? prop.propertyId };
  } catch (error) {
    console.error('Error creating seller property:', error);
    throw error;
  }
}

// Update seller property
export async function updateSellerProperty(id: string, propertyData: Partial<SellerProperty>): Promise<SellerProperty> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellerProperties.update.path, { id })}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(propertyData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update seller property');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating seller property:', error);
    throw error;
  }
}

// Delete seller property
export async function deleteSellerProperty(id: string): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellerProperties.delete.path, { id })}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete seller property');
    }
  } catch (error) {
    console.error('Error deleting seller property:', error);
    throw error;
  }
}

// Approve seller property — PUT /seller-properties/{id}/approve
export async function approveSellerProperty(id: string): Promise<SellerProperty> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellerProperties.approve.path, { id })}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Failed to approve seller property');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error approving seller property:', error);
    throw error;
  }
}

// Reject seller property — PUT /seller-properties/{id}/reject
export async function rejectSellerProperty(id: string, reason: string): Promise<SellerProperty> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.sellerProperties.reject.path, { id })}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ reason }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Failed to reject seller property');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error rejecting seller property:', error);
    throw error;
  }
}

// Seed seller properties
export async function seedSellerProperties(properties: SellerProperty[]): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}/seller-properties/seed`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ properties }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to seed seller properties');
    }

    const data = await response.json();
    console.log(`Seeded ${data.count} seller properties`);
  } catch (error) {
    console.error('Error seeding seller properties:', error);
    throw error;
  }
}

export const sellerPropertyAPI = {
  getSellerProperties,
  getSellerPropertiesBySeller,
  getSellerProperty,
  createSellerProperty,
  updateSellerProperty,
  approveSellerProperty,
  rejectSellerProperty,
  deleteSellerProperty,
  seedSellerProperties,
};
